import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import InfoIcon from '@mui/icons-material/Info'
import { Box, FormControlLabel, Stack, Typography } from '@mui/material'

import FacebookTemplateTextarea from '../../shared/components/ModuleComponents/FacebookTemplateTextarea'

import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import { SaveFacebookPageInformationRequest } from '@3m5/sm_publish-frontend-api'
import {
  saveFacebookPageInformationInitiated, setUpdateImprintAutomaticallyForCurrentUserInitiated
} from '../../store/facebook/facebook.actions'
import { useDispatch, useSelector } from 'react-redux'
import { FacebookPage } from '@3m5/sm_publish-frontend-api'
import { getFacebookPage, getUpdateImprintAutomaticallyValue } from '../../store/facebook/facebook.selector'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { FacebookSettingsRootState } from '../../store/facebookSettingsRoot.reducer'
import { namedRequestInProgress } from '../../store/common/common.selectors'
import SwitchComponent from '@professocial/professocialFormComponents/FormComponents/SwitchComponent'
import {
  SetUpdateImprintAutomaticallyForCurrentUserRequest as OriginalSetUpdateImprintAutomaticallyForCurrentUserRequest
} from '@professocialApi/professocial-core-frontend-api/apis/ImprintApi'

// Extended interface to include networkPageId
interface SetUpdateImprintAutomaticallyForCurrentUserRequest extends OriginalSetUpdateImprintAutomaticallyForCurrentUserRequest {
  networkPageId?: string;
}

interface Props {
  config: FacebookPage | undefined;
  isWizard?: boolean;
  wizardConfirmed?: boolean;
  sendToWizard?: (data: string) => void;
}

// Test
const UserFacebookImpressum: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const [imprintValue, setImprintValue] = useState<string | undefined>(props.config?.customizablePageData?.impressum)
  const [autoUpdate, setAutoUpdate] = useState<boolean>(false)

  const dispatch = useDispatch()
  const dispatchSaveFacebookPage = useCallback((params: SaveFacebookPageInformationRequest) => dispatch(saveFacebookPageInformationInitiated(params)), [dispatch])
  const dispatchSetUpdateImprintAutomaticallyForCurrentUser = useCallback((params: SetUpdateImprintAutomaticallyForCurrentUserRequest) => dispatch(setUpdateImprintAutomaticallyForCurrentUserInitiated(params)), [dispatch])

  const facebookPage: FacebookPage | undefined = useSelector(getFacebookPage)
  const updateImprintAutomaticallyForCurrentUser: boolean = useSelector(getUpdateImprintAutomaticallyValue)
  const savingFacebookPage: boolean = useSelector((state: FacebookSettingsRootState) => namedRequestInProgress(state, saveFacebookPageInformationInitiated.type))

  const saveFacebookImprint = () => {
    if (props.config) {
      const params: SaveFacebookPageInformationRequest = {
        networkPageId: props.config.networkPageId,
        facebookPageCustomInformation: {
          impressum: imprintValue,
        },
      }
      dispatchSaveFacebookPage(params)
    }
  }

  const handleImprint = (desc: string | undefined) => {
    desc ? setImprintValue(desc) : setImprintValue('')
  }

  useEffect(() => {
    if (props.config?.updateImprintAutomatically !== undefined) {
      setAutoUpdate(props.config.updateImprintAutomatically)
    } else {
      setAutoUpdate(updateImprintAutomaticallyForCurrentUser)
    }
  }, [props.config, updateImprintAutomaticallyForCurrentUser])

  useEffect(() => {
    props.wizardConfirmed && imprintValue && props.sendToWizard && props.sendToWizard(imprintValue)
  }, [props.wizardConfirmed])

  useEffect(() => {
    facebookPage && setImprintValue(facebookPage.customizablePageData?.impressum)
  }, [facebookPage])

  return (
    <>
      {!props.isWizard && <Typography variant="h4">{t('facebookSettingsCommon:imprint')}</Typography>}
      <FacebookTemplateTextarea
        maxRows={8}
        text={imprintValue}
        templateText={t('facebookSettingsCommon:imprintTemplate')}
        label={t('facebookSettingsCommon:imprint')}
        updateText={handleImprint}
      />
      <Stack direction="row" alignItems="center">
        <FormControlLabel
          control={
            <SwitchComponent
              checked={autoUpdate}
              color='primary'
              id='active'
              name='active'
              onChange={e => {
                const active = e.target.checked
                if (props.config?.networkPageId) {
                  dispatchSetUpdateImprintAutomaticallyForCurrentUser({ 
                    active,
                    networkPageId: props.config.networkPageId
                  })
                  setAutoUpdate(active)
                }
              }}
            />
          }
          label={`${t('facebookSettingsCommon:autoUpdate')}`}
        />
        <TooltipComponent
          title={`${t('facebookSettingsCommon:autoUpdateDesc')}`}
          type="html"
          child={<InfoIcon color="primary"/>}
          placement="bottom"
          arrow
        />
      </Stack>
      <Box sx={{ textAlign: 'right', width: '100%' }}>
        {!props.isWizard &&
          <PreloaderButton
            position='right'
            loading={savingFacebookPage}
            onClick={saveFacebookImprint}
            text={`${t('facebookSettingsCommon:saveChanges')}`}
          />}
      </Box>
    </>
  )
}
export default UserFacebookImpressum
